import { parametrosDoObjeto } from './parametros'
import { MATERIAIS_TALABARTE, materialDoIndice, type MaterialTalabarte } from './materiaisTalabarte'
import { dobraDoIndice, DOBRAS_NO_ALTO, fatorNaAncoragem, redutorEfetivo, type DobraNoAlto, type TipoRedutor } from './redutoresForca'
import type { CurvaDeForca } from './forcaDeChoque'
import type { OrigemDaQueda, ResultadoQuedaAnimada } from './quedaAnimada'
import type { CenaTabletop } from './tipos'

/**
 * Comparar duas quedas: o instrutor guarda uma queda como referência, muda uma
 * coisa (o material, a massa, o absorvedor, a ancoragem) e simula de novo. O
 * resumo guarda só o que a comparação precisa, para a referência sobreviver às
 * mudanças na cena, e as diferenças de parâmetros dizem o que mudou entre as
 * duas, para a leitura não depender de lembrar o que foi mexido.
 */

export interface ResumoQueda {
  /** Como a queda foi feita: o cálculo animado ou a simulação com física. */
  readonly origem: OrigemDaQueda
  readonly idTrabalhador: string
  readonly nomeTrabalhador: string
  readonly material: MaterialTalabarte
  readonly massaKg: number
  readonly comprimentoTalabarteM: number
  /** O redutor em uso (o absorvedor do trabalhador, o Prusik ou o freio dinâmico), com a abertura e a força dele. */
  readonly redutor: TipoRedutor
  readonly redutorNome: string
  readonly absorvedorAberturaM: number
  readonly forcaAbsorvedorKN: number
  readonly dobra: DobraNoAlto
  readonly forcaNaAncoragemKN: number
  readonly quedaLivreM: number
  readonly forcaPicoKN: number
  readonly pesoKN: number
  /** Do talabarte esticar ao ponto mais baixo. */
  readonly duracaoS: number
  readonly descidaAposEsticarM: number
  readonly aberturaUsadaM: number
  /** Altura final dos pés, pendurado; `null` se bateu. */
  readonly pesFinaisY: number | null
  /** O que atingiu, se bateu. */
  readonly bateuEm: string | null
  readonly curva: CurvaDeForca
}

/** O resumo da queda calculada; `null` se não houve queda (apoiado, talabarte curto) ou o trabalhador sumiu. */
export function resumirQueda(cena: CenaTabletop, resultado: ResultadoQuedaAnimada): ResumoQueda | null {
  const trabalhador = cena.objetos.find((o) => o.id === resultado.idTrabalhador)
  if (!trabalhador || resultado.situacao !== 'ok' || !resultado.choque || !resultado.curvaDeForca) return null
  const p = parametrosDoObjeto(trabalhador)
  const redutor = redutorEfetivo(p)
  const dobra = dobraDoIndice(p.dobraNoAlto!)
  // com física, o que o motor mediu; senão, o cálculo
  const pico = resultado.simulado?.picoKN ?? resultado.choque.forcaPicoKN
  return {
    idTrabalhador: trabalhador.id,
    nomeTrabalhador: trabalhador.nome,
    material: materialDoIndice(p.materialTalabarte!),
    massaKg: p.massaKg!,
    comprimentoTalabarteM: p.comprimentoTalabarteM!,
    redutor: redutor.tipo,
    redutorNome: redutor.nome,
    absorvedorAberturaM: redutor.aberturaM,
    forcaAbsorvedorKN: redutor.forcaKN,
    dobra,
    forcaNaAncoragemKN: pico * fatorNaAncoragem(dobra),
    quedaLivreM: resultado.quedaLivreM,
    origem: resultado.origem,
    forcaPicoKN: pico,
    pesoKN: resultado.choque.pesoKN,
    duracaoS: resultado.curvaDeForca.duracaoS,
    descidaAposEsticarM: resultado.simulado?.descidaAposEsticarM ?? resultado.choque.descidaAposEsticarM,
    aberturaUsadaM: resultado.simulado?.aberturaUsadaM ?? resultado.choque.aberturaAbsorvedorM,
    pesFinaisY: resultado.impacto ? null : resultado.pesFinaisY,
    bateuEm: resultado.impacto?.alvo ?? null,
    curva: resultado.curvaDeForca,
  }
}

export interface DiferencaDeQueda {
  readonly chave: string
  readonly rotulo: string
  readonly antes: string
  readonly depois: string
}

const NOME_DA_ORIGEM: Readonly<Record<OrigemDaQueda, string>> = { calculo: 'cálculo animado', fisica: 'física (Rapier)' }

const numero = (valor: number, unidade: string) => `${Number(valor.toFixed(2)).toLocaleString('pt-BR')} ${unidade}`

/**
 * O que mudou entre a queda de referência e a atual: o trabalhador, o material
 * do talabarte, o comprimento, o absorvedor (abertura e força), a massa e a
 * queda livre (que muda com a ancoragem e com a posição do trabalhador). Só
 * entram os que diferem de verdade.
 */
export function diferencasDeQueda(referencia: ResumoQueda, atual: ResumoQueda): readonly DiferencaDeQueda[] {
  const campos: ReadonlyArray<{ chave: string; rotulo: string; antes: string; depois: string; igual: boolean }> = [
    {
      chave: 'origem',
      rotulo: 'Como foi feita',
      antes: NOME_DA_ORIGEM[referencia.origem],
      depois: NOME_DA_ORIGEM[atual.origem],
      igual: referencia.origem === atual.origem,
    },
    {
      chave: 'trabalhador',
      rotulo: 'Trabalhador',
      antes: referencia.nomeTrabalhador,
      depois: atual.nomeTrabalhador,
      igual: referencia.idTrabalhador === atual.idTrabalhador,
    },
    {
      chave: 'material',
      rotulo: 'Material do talabarte',
      antes: MATERIAIS_TALABARTE[referencia.material].nome,
      depois: MATERIAIS_TALABARTE[atual.material].nome,
      igual: referencia.material === atual.material,
    },
    {
      chave: 'comprimento',
      rotulo: 'Comprimento do talabarte',
      antes: numero(referencia.comprimentoTalabarteM, 'm'),
      depois: numero(atual.comprimentoTalabarteM, 'm'),
      igual: Math.abs(referencia.comprimentoTalabarteM - atual.comprimentoTalabarteM) < 1e-9,
    },
    {
      chave: 'redutor',
      rotulo: 'Redutor de força',
      antes: referencia.redutorNome,
      depois: atual.redutorNome,
      igual: referencia.redutor === atual.redutor,
    },
    {
      chave: 'dobra',
      rotulo: 'Ancoragem',
      antes: DOBRAS_NO_ALTO[referencia.dobra].nome,
      depois: DOBRAS_NO_ALTO[atual.dobra].nome,
      igual: referencia.dobra === atual.dobra,
    },
    {
      chave: 'absorvedor',
      rotulo: 'Abertura do redutor',
      antes: numero(referencia.absorvedorAberturaM, 'm'),
      depois: numero(atual.absorvedorAberturaM, 'm'),
      igual: Math.abs(referencia.absorvedorAberturaM - atual.absorvedorAberturaM) < 1e-9,
    },
    {
      chave: 'forcaAbsorvedor',
      rotulo: 'Força de frenagem do redutor',
      antes: numero(referencia.forcaAbsorvedorKN, 'kN'),
      depois: numero(atual.forcaAbsorvedorKN, 'kN'),
      igual: Math.abs(referencia.forcaAbsorvedorKN - atual.forcaAbsorvedorKN) < 1e-9,
    },
    {
      chave: 'massa',
      rotulo: 'Massa',
      antes: numero(referencia.massaKg, 'kg'),
      depois: numero(atual.massaKg, 'kg'),
      igual: Math.abs(referencia.massaKg - atual.massaKg) < 1e-9,
    },
    {
      chave: 'quedaLivre',
      rotulo: 'Queda livre',
      antes: numero(referencia.quedaLivreM, 'm'),
      depois: numero(atual.quedaLivreM, 'm'),
      // 1 cm: abaixo disso é a mesma queda; a posição do boneco muda um pouco a cada simulação
      igual: Math.abs(referencia.quedaLivreM - atual.quedaLivreM) < 0.01,
    },
  ]
  return campos.filter((c) => !c.igual).map(({ chave, rotulo, antes, depois }) => ({ chave, rotulo, antes, depois }))
}

/**
 * Quantas coisas foram mexidas entre as duas quedas. A queda livre só conta
 * quando muda sozinha (a ancoragem ou a posição do trabalhador): se o
 * comprimento do talabarte mudou, ela é consequência dele. Mais de uma e a
 * comparação mistura causas.
 */
export function causasMexidas(diferencas: readonly DiferencaDeQueda[]): number {
  const chaves = new Set(diferencas.map((d) => d.chave))
  const propria = chaves.has('quedaLivre') && !chaves.has('comprimento') ? 1 : 0
  // trocar de redutor muda a força e a abertura juntas: é uma causa só
  // e o jeito de fazer a queda (cálculo ou física) não é uma causa física
  const consequencias = new Set(['quedaLivre', 'origem', ...(chaves.has('redutor') ? ['absorvedor', 'forcaAbsorvedor'] : [])])
  return diferencas.filter((d) => !consequencias.has(d.chave)).length + propria
}
